import keyConverter from "services/key-converter";
import { createAlert } from "actions/alerts";
import { getVariables } from "services/records/variables";
import Immutable from "immutable";
import {
  toDelete,
  constructResponseReferences,
  isSaved,
  isDirty,
  isActive
} from "reducers/variables/helpers";
import {
  LOCAL_VARIABLE_SCOPE,
  GLOBAL_VARIABLE_SCOPE,
  OAUTH_VARIABLE_SCOPE,
  SENSITIVE_VARIABLE_SCOPE,
  DELETABLE_VARIABLE_SCOPES
} from "constants/variables";
import { getResource } from "selectors/resources";

/*
  TODO:
    - some of these functions dispatch the action,
      while some just create them; make it consistent
*/

export const getAllVariables = ({ responseId, includeInactive = false }) => responseId && {
  CALL_API: {
    method: "get",
    args: {
      responseId
    },
    endpoint: `/variables/?scope=all&response_id=${responseId}&include_inactive=${includeInactive}`,
    types: [
      "GET_ALL_VARIABLES_REQUEST",
      "GET_ALL_VARIABLES_SUCCESS",
      "GET_ALL_VARIABLES_FAILURE"
    ]
  }
};

/**
 */
export const getAllClientVariables = () => ({
  CALL_API: {
    method: "get",
    endpoint: "/variables/",
    types: [
      "GET_ALL_CLIENT_VARIABLES_REQUEST",
      "GET_ALL_CLIENT_VARIABLES_SUCCESS",
      "GET_ALL_CLIENT_VARIABLES_FAILURE"
    ]
  }
});

export const createVariable = (variable, responseId) => ({
  CALL_API: {
    method: "post",
    payload: keyConverter(variable.toJS(), "underscore"),
    args: { variable, responseId },
    endpoint: "/variables/",
    types: [
      "CREATE_VARIABLE_REQUEST",
      "CREATE_VARIABLE_SUCCESS",
      "CREATE_VARIABLE_FAILURE"
    ],
    dispatchCallbacks: [
      {
        request: createAlert,
        fireOnStatus: "error"
      }
    ]
  }
});

export const updateVariable = (variable, responseId) => ({
  CALL_API: {
    method: "patch",
    endpoint: `/variables/${variable.id}/`,
    args: {
      variable,
      responseId
    },
    payload: keyConverter(variable.toJS(), "underscore"),
    types: [
      "UPDATE_VARIABLE_REQUEST",
      "UPDATE_VARIABLE_SUCCESS",
      "UPDATE_VARIABLE_FAILURE"
    ],
    dispatchCallbacks: [
      {
        request: createAlert,
        fireOnStatus: "error"
      }
    ]
  }
});

export const deleteVariable = (variable, responseId) => ({
  CALL_API: {
    method: "delete",
    endpoint: `/variables/${variable.id}/`,
    args: {
      variable,
      responseId
    },
    types: [
      "DELETE_VARIABLE_REQUEST",
      "DELETE_VARIABLE_SUCCESS",
      "DELETE_VARIABLE_FAILURE"
    ],
    dispatchCallbacks: [
      {
        request: createAlert,
        fireOnStatus: "error"
      }
    ]
  }
});

const saveVariables = (variables, responseId) => dispatch => variables.map(variable => (
  dispatch(createVariable(variable, responseId))
));

const updateVariables = (variables, responseId) => dispatch => variables.map(variable => (
  dispatch(updateVariable(variable, responseId))
));

export const deleteVariables = (variables, responseId) => dispatch => variables.map(variable => (
  dispatch(deleteVariable(variable, responseId))
));

const getExistingGlobalVar = (state, variable) => state.variables
  .get(GLOBAL_VARIABLE_SCOPE)
  .find(global => (
    global.get("id") !== variable.id &&
    global.get("name") === variable.name &&
    global.get("isValid")
  ));

/**
 * Returns existing local variable with same name if it exists
 * @param {Object} state
 * @param {Immutable.Record} variable
 * @param {String} responseId
 * @returns {Immutable.Record|undefined}
 */
const getExistingLocalVar = (state, variable, responseId) => {
  if (!state.variables.get(LOCAL_VARIABLE_SCOPE).has(responseId)) {
    return null;
  }

  return state.variables
    .getIn([LOCAL_VARIABLE_SCOPE, responseId])
    .find(local => (
      local.get("id") !== variable.id &&
      local.get("name") === variable.name &&
      local.get("isValid")
    ));
};

/**
 * Get the corresponding local or global variable if it exists
 * @param {Object} state
 * @param {Immutable.Record} variable
 * @param {String} responseId
 * @returns {Immutable.Record | undefined}
 */
const getExistingVariable = (state, variable, responseId) => {
  const existingVarFn = variable.scope === GLOBAL_VARIABLE_SCOPE ? getExistingGlobalVar : getExistingLocalVar;
  const existingVar = existingVarFn(state, variable, responseId);

  return existingVar;
};


export const createUnsavedVariable = ({
  variable,
  responseId,
  messageIndex,
  keyPath = [],
  variableListIndex
}) => (dispatch, getState) => {
  const state = getState();
  let existingVar = getExistingVariable(state, variable, responseId);

  if (existingVar) {
    existingVar = existingVar.merge({
      key: variable.key
    });

    if (!isActive(existingVar)) {
      existingVar = existingVar.merge({
        type: variable.type
      });
    }
  }

  dispatch({
    type: "CREATE_UNSAVED_VARIABLE",
    variable,
    responseId,
    messageIndex,
    existingVar,
    keyPath,
    variableListIndex
  });
};


export const setDropdownVariable = ({
  variable,
  newVariable,
  responseId,
  messageIndex,
  keyPath,
  variableListIndex
}) => ({
  type: "SET_DROPDOWN_VARIABLE",
  variable,
  newVariable,
  responseId,
  messageIndex,
  keyPath,
  variableListIndex
});

/**
 * Action to update properties of `variable` to that of `newVariable` within state
 *
 * @param {Object} p
 * @param {Immutable.Record} p.variable
 * @param {Immutable.Record} p.newVariable
 * @param {String} p.responseId
 * @param {Number} p.messageIndex
 * @param {(String | Number)[]} p.keyPath
 * @returns {AsyncAction}
 */
export const updateVariableState = ({
  variable,
  newVariable,
  responseId,
  messageIndex,
  keyPath = []
}) => (dispatch, getState) => {
  const state = getState();

  let updatedVar = newVariable;
  const existingVar = getExistingVariable(state, newVariable, responseId);

  if (!newVariable.equals(variable)) {
    updatedVar = updatedVar.merge({
      modified: true,
      isValid: Boolean(updatedVar.get("name"))
    });
    dispatch({
      type: "UPDATE_VARIABLE",
      variable,
      updatedVar,
      existingVar,
      responseId,
      messageIndex,
      keyPath
    });
  }
};

/**
 * @param {Object} variable
 * @param {String} responseId
 * @param {Number} messageIndex
 * @param {Array.<String|Number>} keyPath
 * @returns {Object}
 */
export function deleteVariableState(variable, responseId, messageIndex, keyPath) {
  return {
    type: "DELETE_VARIABLE",
    variable,
    responseId,
    messageIndex,
    keyPath
  };
}

export const getAuths = () => ({
  CALL_API: {
    method: "get",
    endpoint: "/auths/",
    types: ["GET_AUTHS_REQUEST", "GET_AUTHS_SUCCESS", "GET_AUTHS_FAILURE"]
  }
});

const groupVariablesByAction = (allVariables, responseId) => allVariables
  .map((variable) => {
    if (
      !variable.responseReferenceCounts.has(responseId) ||
      (variable.getIn(["responseReferenceCounts", responseId, "unsaved"]) === 0 &&
      !variable.get("modified"))
    ) {
      /*
            This variable has no relation to this responseId or
            there are no unsaved changes that need to be made for this responseId
          */
      return { type: "other", variable };
    }

    if (!isSaved(variable)) {
      return { type: "create", variable };
    }

    if (toDelete(variable, responseId)) {
      return { type: "delete", variable };
    }

    if (isDirty(variable, responseId)) {
      return { type: "update", variable };
    }

    return { type: "other", variable };
  })
  .groupBy(variableGroup => variableGroup.type)
  .map(variables => variables.map(v => v.variable));

const getAllVariablesFlat = (state, responseId) => (
  Immutable.List()
    .concat(
      state.variables.getIn([LOCAL_VARIABLE_SCOPE, responseId]),
      state.variables.get(GLOBAL_VARIABLE_SCOPE),
      state.variables.get(OAUTH_VARIABLE_SCOPE),
      state.variables.get(SENSITIVE_VARIABLE_SCOPE)
    ).filter(Boolean)
);

/**
 * Modifies variables appropriately before saving to API
 * @param {Immutable.List} allVariables
 * @param {String} responseId
 * @returns {Immutable.List}
 */
function prepareVariables(allVariables, responseId) {
  return allVariables.map((variable) => {
    const responseReferences = constructResponseReferences(variable, responseId);

    return variable.merge({
      responseReferences
    });
  });
}

/**
 * @param {string} responseId
 * @param {Dispatch} dispatch
 * @param {function(): State} getState
 * @returns {Promise<unknown>}
 */
export function getVariablesPromises(responseId, dispatch, getState) {
  const state = getState();

  /** @type Client */
  const client = getResource(state, "client");

  if (!client.features.get("personalization")) {
    return Promise.resolve();
  }

  const emptyList = Immutable.List();

  let allVariables = getAllVariablesFlat(state, responseId);
  allVariables = prepareVariables(allVariables, responseId);
  const groupedVariables = groupVariablesByAction(allVariables, responseId);

  const variablesToUpdate = groupedVariables.get("update", emptyList).map((variable) => {
    let newVariable = variable;

    if (variable.scope === OAUTH_VARIABLE_SCOPE) {
      const responseReferences = variable.responseReferences.filter(
        reference => reference
      );
      newVariable = variable.set("responseReferences", responseReferences);

      if (variable.responseReferenceCounts.get("undefined")) {
        const responseReferenceCounts = variable.responseReferenceCounts.remove("undefined");
        newVariable = variable.set("responseReferenceCounts", responseReferenceCounts);
      }
    }

    return newVariable;
  });

  // Wait for variables to save, delete, and update before publishing response changes
  let variablesPromises = [
    dispatch(saveVariables(groupedVariables.get("create", emptyList), responseId)),
    dispatch(deleteVariables(groupedVariables.get("delete", emptyList), responseId)),
    dispatch(updateVariables(variablesToUpdate, responseId))
  ];
  variablesPromises = variablesPromises.reduce(
    (acc, list) => acc.concat(list), Immutable.List()
  ).toJS();

  return Promise.all(variablesPromises);
}

/**
 * Returns the nested messages within a conditional as well as the path to reach it
 * @param {Immutable.Record} message
 * @param {Array} keyPath
 * @returns {Immutable.List}
 */
export const getConditionalNestedMessages = (message, keyPath) => {
  if (message.type !== "conditionals_block") {
    return Immutable.List();
  }

  let messages = Immutable.List();
  message.get("statements").forEach((statement, statementIndex) => {
    statement.get("messages").forEach((nestedMessage, nestedMessageIndex) => {
      const deepPath = ["statements", statementIndex, "messages", nestedMessageIndex];
      messages = messages.push(Immutable.Map({
        path: keyPath.concat(deepPath),
        message: nestedMessage
      }));
    });
  });

  return messages;
};

/**
 * Returns the nested messages within a scheduled block as well as the path to reach it
 * @param {Immutable.Record} message
 * @param {Array} keyPath
 * @returns {Immutable.List}
 */
export const getScheduleNestedMessages = (message, keyPath) => {
  if (message.type !== "scheduled_block") {
    return Immutable.List();
  }

  let messages = Immutable.List();

  ["affirmativeMessages", "negativeMessages"].forEach((type) => {
    message.get(type).forEach((msg, i) => {
      const deepPath = [type, i];
      messages = messages.push(Immutable.Map({
        path: keyPath.concat(deepPath),
        message: msg
      }));
    });
  });

  return messages;
};

/**
 * Returns an array of variable ids attached to message
 * @param {Immutable.Record} message a response message
 */
function getVariableIdsFromMessage(message) {
  let variableIds;

  switch (message.type) {
    case "capture":
      // fallsthrough

    case "list_selection_template": {
      variableIds = [message.get("variableId")];
      break;
    }

    case "variable_override":
      // fallsthrough

    case "widget":
    // fallsthrough

    case "http_request_recipe": {
      variableIds = message.get("variablesData")
        .map(v => v.get("variableId"));
      break;
    }

    default: {
      variableIds = [];
    }
  }

  return variableIds;
}

/**
 * Deletes a given messages variables _in state_
 * @param {Object} params
 * @param {String} params.responseId
 * @param {Number} params.messageIndex
 * @param {String[]} params.keyPath
 */
export const deleteMessageVariables = ({
  responseId, messageIndex, keyPath
}) => (
  dispatch,
  getState
) => {
  const state = getState();
  const { responsesLoaded, variables } = state;
  const allVariables = getVariables(variables, {
    scopes: DELETABLE_VARIABLE_SCOPES,
    responseId
  });

  // replace keypath for responsesloaded
  const newKeypath = keyPath;
  newKeypath[0] = responseId;

  const message = responsesLoaded.getIn(newKeypath);
  let messagesToDelete;

  switch (message.type) {
    case "conditionals_block":
      messagesToDelete = getConditionalNestedMessages(message, keyPath);
      break;
    case "scheduled_block":
      messagesToDelete = getScheduleNestedMessages(message, keyPath);
      break;
    case "capture":
    case "variable_override":
    case "widget":
    case "http_request_recipe":
    case "list_selection_template":
      messagesToDelete = [Immutable.Map({
        path: keyPath,
        message
      })];
      break;
    default:
      break;
  }

  if (!messagesToDelete) {
    return;
  }

  messagesToDelete = messagesToDelete.filter(msg => (
    [
      "capture",
      "http_request_recipe",
      "variable_override",
      "widget",
      "list_selection_template"
    ].includes(msg.getIn(["message", "type"]))
  ));


  messagesToDelete.forEach((msg) => {
    const currentMsg = msg.get("message");
    const currentPath = msg.get("path");

    const variableIds = getVariableIdsFromMessage(currentMsg);

    const variablesToDelete = allVariables
      .filter(variable => variableIds.includes(variable.get("id")));

    variablesToDelete.forEach(variable => dispatch(
      deleteVariableState(variable, responseId, messageIndex, currentPath)
    ));
  });
};

/**
 * Deletes all variables associated with the response
 * @param {Function} getState
 * @param {Function} dispatch
 * @param {String} responseId
 * @param {String} langToDelete - Only delete for this language if specified
 * @returns {Promise}
 */
export const deleteResponseVariables = (getState, dispatch, responseId, langToDelete = null) => {
  const state = getState();

  /** @type Client */
  const client = getResource(state, "client");

  if (!client.features.get("personalization")) {
    return Promise.resolve();
  }

  const { responsesLoaded } = state;
  const response = responsesLoaded.get(responseId);
  const languagesToDelete = langToDelete ? [langToDelete] : response.messages.keySeq();

  languagesToDelete.forEach((language) => {
    response.getIn(["messages", language]).forEach((message, messageIndex) => {
      const keyPath = [responseId, "messages", language, messageIndex];
      dispatch(
        deleteMessageVariables({
          responseId, messageIndex, keyPath
        })
      );
    });
  });

  // when we delete a language, don't save to the backend
  if (!langToDelete) {
    return getVariablesPromises(responseId, dispatch, getState);
  }

  return Promise.resolve();
};
